package pl.nickers.match3.ecs

import kotlin.random.Random

/**
 * GRAVITY phase: generates new jellies for each spawn point.
 *
 * **Above-board spawn points (row < 0):** Spawns one jelly per above-board
 * spawn point per GRAVITY pass at row 0 if the cell is empty and not a hole.
 * Auto-created invisible spawn points at row -1 cover every column for randomly
 * generated maps.
 *
 * **On-board spawn points (row ≥ 0):** Fill only the cell directly below when empty.
 *
 * Respects [BoardSpawnControlComponent.disableNewJellySpawn]. Spawned entities
 * receive a [FallingComponent] so gravity picks them up immediately.
 */
internal class SpawnSystem(
    private val random: Random = Random.Default,
    private val catalog: EntityCatalog = EntityCatalog.default(),
) : BaseSystem() {
    private lateinit var posMapper: ComponentMapper<GridPositionComponent>
    private lateinit var fallingMapper: ComponentMapper<FallingComponent>

    override fun initialize() {
        posMapper = world.mapper()
        fallingMapper = world.mapper()
    }

    override fun processSystem() {
        val board = world.boardState() ?: return
        if (board.phase != GamePhase.GRAVITY) return
        if (world.boardSpawnControl()?.disableNewJellySpawn == true) return

        val beforeFalling = world.getEntitiesForAspect(Aspect.all(FallingComponent::class)).count()

        val gridHeight = board.height
        val aspect = Aspect.all(SpawnPointComponent::class, GridPositionComponent::class)

        for (entityId in world.getEntitiesForAspect(aspect)) {
            val pos = posMapper[entityId] ?: continue
            val spawnCol = pos.col

            if (pos.row < 0) {
                if (world.isHole(0, spawnCol)) continue
                if (world.findEntityAt(0, spawnCol) != null) continue
                val newEntity = world.createRandomBoardEntity(
                    row = 0, col = spawnCol, random = random, catalog = catalog,
                )
                fallingMapper.set(
                    newEntity,
                    FallingComponent(
                        fromRow = -1,
                        toRow = 0,
                        fromCol = spawnCol,
                        toCol = spawnCol,
                    ),
                )
            } else {
                // On-board spawn point: fill only the cell directly below.
                val spawnRow = pos.row + 1
                if (spawnRow !in 0 until gridHeight) continue
                if (world.isHole(spawnRow, spawnCol)) continue
                if (world.findEntityAt(spawnRow, spawnCol) != null) continue

                val newEntity = world.createRandomBoardEntity(
                    row = spawnRow, col = spawnCol, random = random, catalog = catalog,
                )
                fallingMapper.set(
                    newEntity,
                    FallingComponent(
                        fromRow = pos.row,
                        toRow = spawnRow,
                        fromCol = spawnCol,
                        toCol = spawnCol,
                    ),
                )
            }
        }
        val afterFalling = world.getEntitiesForAspect(Aspect.all(FallingComponent::class)).count()
        if (afterFalling > beforeFalling) board.didWork = true
    }
}
